/* ⛔ VARIANTE D'UN QUIZ — SOURCE UNIQUE. Audit de taxonomie du 18/09/2026,
 * complete le 21/09/2026 par une regle de repli (arbitrage A5, Hicham).
 *
 * THE LAB n'a qu'un seul type de quiz (`type: 'Quizz'`). La distinction
 * QCM / Vrai-Faux est portee par `answers.quizVariant`, au meme endroit et de la
 * meme facon qu'InputTexte porte deja la sienne (`answers.type`) — donc SANS
 * migration de schema : `answers` est une colonne JSON existante, deja persistee.
 *
 * Ce module est volontairement un util NEUTRE (aucun import de composant, de MUI
 * ni d'image) : le gabarit `listFormats`, le panneau d'edition et le lecteur en
 * dependent tous les trois. Une seule definition, pas trois litteraux qui derivent.
 *
 * ── POURQUOI UN REPLI A ETE AJOUTE ────────────────────────────────────────────
 * La regle du 18/09 etait « champ absent => qcm », pour que l'historique ne
 * change pas de rendu. Mesure du 21/09 sur les 74 modules de LAB : sur
 * 1283 ecrans de quiz, **3 seulement** portent `answers.quizVariant`. Les
 * Vrai/Faux historiques etaient donc tous rendus en QCM — reponses EMPILEES et
 * NUMEROTEES, alors que la planche Figma 09 les veut COTE A COTE et sans
 * numero. Ce n'etait pas un defaut d'integration : c'etait l'effet exact de la
 * regle. Arbitrage rendu : on ne laisse pas le format 09 non conforme, et on ne
 * lance pas de migration de contenu.
 *
 * ── LA REGLE, DANS CET ORDRE ──────────────────────────────────────────────────
 * 1. `answers.quizVariant` renseigne  => il PRIME TOUJOURS, y compris pour dire
 *    'qcm' sur un ecran dont les libelles ressemblent a un Vrai/Faux.
 * 2. Champ absent ET les deux libelles forment EXACTEMENT une paire reconnue
 *    ci-dessous => 'vrai_faux'.
 * 3. Sinon => 'qcm'.
 *
 * ⛔ ON NE DEDUIT JAMAIS DU NOMBRE DE REPONSES. L'audit du 18/09 avait mesure
 * que « 2 reponses => Vrai/Faux » serait FAUX sur 81 ecrans reels
 * (canard/lapin, vase/visages, sens de rotation, photographie/reconstruction...).
 * Le repli porte sur les LIBELLES, et sur eux seuls.
 *
 * ── CONTROLE SUR DONNEES REELLES, 21/09/2026, 74 modules de LAB ───────────────
 *   1283 ecrans de quiz, dont 422 a exactement deux reponses, 43 paires distinctes.
 *   347 reclasses en 'vrai_faux'      (vrai/faux 179+, oui/non 119, yes/no 33, true/false 18)
 *   933 restent 'qcm'                 dont TOUS les pieges ci-dessus
 *     2 deja explicites 'vrai_faux'   inchanges
 *     1 explicite 'qcm'               PRESERVE (l'explicite prime)
 *   Aucun ecran a 3 reponses ou plus n'est touche : la regle exige exactement deux.
 */
export const QUIZ_QCM = "qcm"
export const QUIZ_VRAI_FAUX = "vrai_faux"

/* Paires reconnues. Comparaison sur l'ENSEMBLE des deux libelles : l'ordre
   d'affichage reste celui du contenu, « Non / Oui » est donc reconnu comme
   « Oui / Non ». Normalisation minimale et previsible : espaces retires,
   minuscules. Aucune tolerance sur la ponctuation ni sur les accents — une
   paire qui n'est pas ecrite ainsi reste un QCM, ce qui est le cote sur. */
export const PAIRES_VRAI_FAUX = [
  ["vrai", "faux"],
  ["oui", "non"],
  ["yes", "no"],
  ["true", "false"],
]

const normaliser = v => String(v == null ? "" : v).trim().toLowerCase()

/** Les deux libelles forment-ils une paire Vrai/Faux reconnue ? */
export function libellesVraiFaux(responses) {
  if (!Array.isArray(responses) || responses.length !== 2) return false
  const [a, b] = responses.map(r => normaliser(r && r.reponse))
  if (!a || !b || a === b) return false
  /* `yes` + `true` ne forme PAS une paire : les deux libelles doivent venir de
     la MEME paire, l'un a chaque bout. */
  return PAIRES_VRAI_FAUX.some(([x, y]) => (a === x && b === y) || (a === y && b === x))
}

export function quizVariant(format) {
  const explicite = format && format.answers ? format.answers.quizVariant : undefined
  if (explicite === QUIZ_VRAI_FAUX) return QUIZ_VRAI_FAUX
  if (explicite === QUIZ_QCM) return QUIZ_QCM
  return libellesVraiFaux(format && format.responses) ? QUIZ_VRAI_FAUX : QUIZ_QCM
}
